import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { setWorkspaceIntegrations } from "@/store/integrations/integrations-slice";
import { useAppDispatch } from "@/store/store-hooks";
import { FaroButton, FaroDialog, FaroText, useToast } from "@faro-lotv/flat-ui";
import {
  BcfServicesIntegrationType,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, useRef, useState, type JSX } from "react";
import {
  getExternalAnnotationProviderName,
  getWorkspaceIntegrationProviderId,
} from "../external-annotation-utils";
import { ProviderAuthorizationService } from "./provider-authorization-service";

/** Props for the ProviderAuthorization component */
export type ProviderAuthorizationProps = {
  /** The BCF Services integration type of the associated external annotation*/
  integrationType: BcfServicesIntegrationType;

  /** Optional callback to be invoked when the token is updated */
  onTokenUpdated?(): void;

  /** Optional callback to be invoked when the authorization button is clicked */
  onAuthorizationButtonClicked?(): void;
};

/**
 * Component to handle the authorization of an integration provider of external annotations.
 *
 * @returns A button to authorize the integration provider and a dialog to guide the user through the process.
 */
export function ProviderAuthorization({
  integrationType,
  onTokenUpdated,
  onAuthorizationButtonClicked,
}: ProviderAuthorizationProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorHandlers();
  const { coreApiClient } = useApiClientContext();

  const [isAuthorizing, setIsAuthorizing] = useState<boolean>(false);
  const providerId = getWorkspaceIntegrationProviderId(integrationType);
  const providerName = getExternalAnnotationProviderName(integrationType);

  const providerAuthorizationService = useRef<ProviderAuthorizationService>(
    new ProviderAuthorizationService({
      baseUrl: coreApiClient.baseUrl,
      providerId,
    }),
  );

  /**
   * Updates the user tokens for workspace integrations in the store.
   *
   * @returns A promise that resolves to true if the update was successful, false otherwise.
   */
  const updateUserTokens = useCallback(async (): Promise<boolean> => {
    try {
      const usersTokens = await coreApiClient.getUsersTokens();
      dispatch(setWorkspaceIntegrations(usersTokens));
      return true;
    } catch {
      // Ignore errors when updating the tokens as it's not critical
      // but return false to indicate the update failed
      return false;
    }
  }, [coreApiClient, dispatch]);

  /**
   * Handles the authorization flow when the user clicks the authorize button and
   * updates the user tokens in the store upon successful authorization.
   */
  const authorizeAndUpdate = useCallback(async (): Promise<void> => {
    onAuthorizationButtonClicked?.();
    setIsAuthorizing(true);
    try {
      const isAuthorizationSuccessful =
        await providerAuthorizationService.current.authorizeProvider();

      // Only update the tokens if the authorization was successful
      let message: string | undefined;
      let isUpdateSuccessful = false;
      if (isAuthorizationSuccessful) {
        isUpdateSuccessful = await updateUserTokens();
        if (!isUpdateSuccessful) {
          message = "Please reload the page to see the changes.";
        }
      }

      const title = isAuthorizationSuccessful
        ? `${providerName} authorization was successful.`
        : `${providerName} authorization was canceled.`;

      const variant =
        isAuthorizationSuccessful && isUpdateSuccessful ? "success" : "warning";

      openToast({
        title,
        message,
        variant,
      });

      onTokenUpdated?.();
    } catch (error) {
      handleErrorWithToast({
        title: `Failed to authorize ${providerName}.`,
        error,
      });
    }

    setIsAuthorizing(false);
  }, [
    handleErrorWithToast,
    onTokenUpdated,
    openToast,
    providerName,
    updateUserTokens,
    onAuthorizationButtonClicked,
  ]);

  /**
   * Cancels the authorization flow by closing the authorization window and hiding the dialog.
   */
  const cancelAuthorization = useCallback(() => {
    providerAuthorizationService.current.closeAuthorizationWindow();
    setIsAuthorizing(false);
  }, []);

  return (
    <>
      <FaroButton onClick={authorizeAndUpdate}>Authorize</FaroButton>
      <FaroDialog
        open={isAuthorizing}
        onCancel={cancelAuthorization}
        title={`Authorize ${providerName}`}
        dark
      >
        <FaroText variant="bodyM" dark>
          Please complete the sign-in and authorization in the window that just
          opened. If you don`t see it, check your pop-up settings.
        </FaroText>
      </FaroDialog>
    </>
  );
}
